import React, { useState, useEffect } from 'react';
import { Button, Input } from './UIComponents';
import { Activity, ArrowRight, ArrowLeft, Mail, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

interface AuthProps {
  mode: 'login' | 'signup';
  onComplete: () => void;
  onSwitchMode: () => void;
  isModal?: boolean;
}

export const Auth: React.FC<AuthProps> = ({ mode, onComplete, onSwitchMode, isModal = false }) => {
  const { signUp, signIn, resetPassword, resendEmailConfirmation } = useAuth();
  const [internalMode, setInternalMode] = useState<'default' | 'forgot' | 'email-confirmation-pending'>('default');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Reset internal state when parent mode changes
  useEffect(() => {
    setInternalMode('default');
    setError(null);
    setSuccessMessage(null);
    setShowPassword(false);
    setShowConfirmPassword(false);
  }, [mode]);

  // Password validation function
  const validatePassword = (pwd: string): string | null => {
    if (pwd.length < 6) {
      return 'Password must be at least 6 characters.';
    }
    const hasLowercase = /[a-z]/.test(pwd);
    const hasUppercase = /[A-Z]/.test(pwd);
    const hasNumber = /[0-9]/.test(pwd);

    if (!hasLowercase || !hasUppercase || !hasNumber) {
      return 'Password must include at least one uppercase letter, one lowercase letter, and one number.';
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    if (internalMode === 'forgot') {
      if (!email) {
        setError('Please enter your email address.');
        return;
      }
      setIsLoading(true);
      const { error } = await resetPassword(email);
      setIsLoading(false);

      if (error) {
        setError(error.message);
      } else {
        setSuccessMessage('If an account exists with this email, you will receive a reset link shortly.');
      }
      return;
    }

    if (!email || !password) {
      setError('Please fill in all fields.');
      return;
    }

    if (mode === 'signup') {
      const passwordError = validatePassword(password);
      if (passwordError) {
        setError(passwordError);
        return;
      }
      if (password !== confirmPassword) {
        setError('Passwords do not match.');
        return;
      }
    }

    setIsLoading(true);

    try {
      if (mode === 'signup') {
        const { error } = await signUp(email, password, name || undefined);
        if (error) {
          setError(error.message);
          setIsLoading(false);
        } else {
          // User created successfully, but needs to confirm email
          // Show "check your email" message instead of redirecting
          setIsLoading(false);
          setInternalMode('email-confirmation-pending');
        }
      } else {
        const { error } = await signIn(email, password);
        if (error) {
          setError(error.message);
          setIsLoading(false);
        } else {
          setIsLoading(false);
          onComplete();
        }
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred');
      setIsLoading(false);
    }
  };

  const handleResendConfirmation = async () => {
    if (!email) return;

    setIsResending(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const { error } = await resendEmailConfirmation(email);
      if (error) {
        setError(error.message);
      } else {
        setSuccessMessage('Confirmation email sent! Please check your inbox.');
        setTimeout(() => setSuccessMessage(null), 5000);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to resend confirmation email.');
    } finally {
      setIsResending(false);
    }
  };

  const renderContent = () => {
    if (internalMode === 'email-confirmation-pending') {
      return (
        <>
          <div className="flex justify-center mb-6">
            <div className="w-12 h-12 bg-zinc-900 rounded-xl flex items-center justify-center text-white border border-zinc-800 shadow-glow">
              <Mail size={24} strokeWidth={2.5} />
            </div>
          </div>
          <h1 className="text-2xl font-medium text-center text-white mb-2">Check Your Email</h1>
          <p className="text-zinc-500 text-center text-[13px] mb-6">
            We've sent a confirmation email to <span className="text-zinc-300 font-medium">{email}</span>. Please check your inbox and click the confirmation link to continue.
          </p>

          {error && (
            <div className="text-red-400 text-[12px] bg-red-950/20 border border-red-900/30 p-2 rounded text-center mb-4">
              {error}
            </div>
          )}

          {successMessage && (
            <div className="text-emerald-400 text-[12px] bg-emerald-950/20 border border-emerald-900/30 p-2 rounded text-center mb-4">
              {successMessage}
            </div>
          )}

          <div className="space-y-3">
            <Button
              onClick={handleResendConfirmation}
              className="w-full"
              size="lg"
              isLoading={isResending}
            >
              Resend Confirmation Email
            </Button>

            <div className="pt-6 border-t border-zinc-900 text-center">
              <button
                onClick={() => {
                  setInternalMode('default');
                  setError(null);
                  setSuccessMessage(null);
                  setEmail('');
                  setPassword('');
                  setConfirmPassword('');
                  setName('');
                  onSwitchMode();
                }}
                className="text-[13px] text-zinc-400 hover:text-white font-medium transition-colors flex items-center justify-center gap-2 mx-auto"
              >
                <ArrowLeft size={14} /> Back to Login
              </button>
            </div>
          </div>
        </>
      );
    }

    if (internalMode === 'forgot') {
      return (
        <>
          <div className="flex justify-center mb-6">
            <div className="w-12 h-12 bg-zinc-900 rounded-xl flex items-center justify-center text-white border border-zinc-800 shadow-glow">
              <Mail size={24} strokeWidth={2.5} />
            </div>
          </div>
          <h1 className="text-2xl font-medium text-center text-white mb-2">Reset Password</h1>
          <p className="text-zinc-500 text-center text-[13px] mb-8">
            Enter your email and we'll send you a reset link.
          </p>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-[11px] font-medium text-zinc-500 uppercase tracking-wide">Email</label>
              <Input
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
              />
            </div>
            {error && (
              <div className="text-red-400 text-[12px] bg-red-950/20 border border-red-900/30 p-2 rounded text-center">
                {error}
              </div>
            )}
            {successMessage && (
              <div className="text-emerald-400 text-[12px] bg-emerald-950/20 border border-emerald-900/30 p-2 rounded text-center">
                {successMessage}
              </div>
            )}
            <Button type="submit" className="w-full mt-2" size="lg" isLoading={isLoading}>
              Send Reset Link
            </Button>
          </form>
          <div className="mt-6 pt-6 border-t border-zinc-900 text-center">
            <button
              onClick={() => { setInternalMode('default'); setError(null); setSuccessMessage(null); }}
              className="text-[13px] text-zinc-400 hover:text-white font-medium transition-colors flex items-center justify-center gap-2 mx-auto"
            >
              <ArrowLeft size={14} /> Back to Sign In
            </button>
          </div>
        </>
      );
    }

    return (
      <>
        <div className="flex justify-center mb-6">
          <div className="w-12 h-12 bg-zinc-900 rounded-xl flex items-center justify-center text-white border border-zinc-800 shadow-glow">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5 5L12 19L19 5" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        </div>

        <h1 className="text-2xl font-medium text-center text-white mb-2">
          {mode === 'login' ? 'Welcome back' : 'Create an account'}
        </h1>
        <p className="text-zinc-500 text-center text-[13px] mb-8">
          {mode === 'login'
            ? 'Enter your credentials to access your console.'
            : 'Get started with Verline today.'}
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === 'signup' && (
            <div className="space-y-1.5 animate-fade-in">
              <label className="text-[11px] font-medium text-zinc-500 uppercase tracking-wide">Name (optional)</label>
              <Input
                type="text"
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
          )}
          <div className="space-y-1.5">
            <label className="text-[11px] font-medium text-zinc-500 uppercase tracking-wide">Email</label>
            <Input
              type="email"
              placeholder="you@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
            />
          </div>

          <div className="space-y-1.5">
            <div className="flex justify-between items-center">
              <label className="text-[11px] font-medium text-zinc-500 uppercase tracking-wide">Password</label>
              {mode === 'login' && (
                <button type="button" onClick={() => { setInternalMode('forgot'); setError(null); }} className="text-[11px] text-zinc-400 hover:text-white transition-colors">
                  Forgot password?
                </button>
              )}
            </div>
            <div className="relative">
              <Input
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-200 transition-colors focus:outline-none"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          {mode === 'signup' && (
            <div className="space-y-1.5 animate-fade-in">
              <label className="text-[11px] font-medium text-zinc-500 uppercase tracking-wide">Confirm Password</label>
              <div className="relative">
                <Input
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-200 transition-colors focus:outline-none"
                  aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                >
                  {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
          )}

          {error && (
            <div className="text-red-400 text-[12px] bg-red-950/20 border border-red-900/30 p-2 rounded text-center">
              {error}
            </div>
          )}

          {successMessage && (
            <div className="text-emerald-400 text-[12px] bg-emerald-950/20 border border-emerald-900/30 p-2 rounded text-center">
              {successMessage}
            </div>
          )}

          <Button type="submit" className="w-full mt-2" size="lg" isLoading={isLoading}>
            {mode === 'login' ? 'Sign In' : 'Create Account'} <ArrowRight size={16} className="ml-2" />
          </Button>
        </form>

        <div className="mt-6 pt-6 border-t border-zinc-900 text-center">
          <p className="text-[13px] text-zinc-500">
            {mode === 'login' ? "Don't have an account?" : "Already have an account?"}
            <button
              onClick={() => { setError(null); onSwitchMode(); }}
              className="ml-2 text-zinc-200 hover:text-white font-medium transition-colors"
            >
              {mode === 'login' ? 'Sign up' : 'Log in'}
            </button>
          </p>
        </div>
      </>
    );
  };

  // When in modal mode, we strip the outer containers since the Modal component provides them
  if (isModal) {
    return <div className="w-full">{renderContent()}</div>;
  }

  return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center p-6 animate-fade-in relative">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-zinc-900/20 blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-indigo-900/10 blur-[120px]" />
      </div>

      <div className="w-full max-w-md bg-zinc-950 border border-zinc-800 rounded-xl p-8 shadow-2xl relative z-10">
        {renderContent()}
      </div>
    </div>
  );
};